// TESSERAX — STRATES : la COLONNE VERTÉBRALE roguelite (cœur pur, testé).
//
// Les modes endless (libre/défi/zen) sont des « va le plus loin » : pas de DESTINATION, le score est
// la seule jauge. L'échelle de défis (core/challenges) propose bien des tableaux autoraux, mais ISOLÉS
// (une graine, un objectif, une fin). Les STRATES étendent ce concept en une ASCENSION CONTINUE façon
// Slay-the-Spire : une run traverse une suite de strates thématiques ; chaque strate a un OBJECTIF, un
// MUTATEUR propre (cf. core/mutators) et, parfois, un GARDIEN (configuration de départ pré-placée à
// démanteler). Valider une strate fait monter l'ascension et REPORTE les reliques acquises → une raison
// de revenir qui n'est pas « bats ton propre nombre », et un terrain où le Pli/les reliques se déploient.
//
// GARDIEN ≠ MONTÉE D'INSTABILITÉ (gelée). Un gardien est un remplissage STATIQUE, posé UNE fois à
// l'ouverture de la strate (comme la position de départ d'un casse-tête) : aucun sol qui monte, aucun
// bloc parasite injecté en cours de partie, RIEN de core/pressure. C'est une géométrie d'ouverture,
// point. Cette distinction est volontaire et doit le rester.
//
// Module PUR : ni THREE ni DOM, pas de Math.random. main.ts lit le catalogue, applique le mutateur via
// ses leviers de run existants, pose le gardien dans la grille, et persiste l'avancement dans la méta.
import { GRID, HEIGHT } from './constants';
import type { ChallengeGoalKind } from './challenges';

export interface Stratum {
  id: string;             // stable (clé i18n strate.<id>.* + persistance méta)
  name: string;           // FR (repli) — EN via i18n
  text: string;           // une ligne : l'objectif décrit
  goalKind: ChallengeGoalKind;
  goalN: number;
  mutator: string;        // id d'un core/mutators.MUTATORS appliqué pendant la strate
  guardian: number;       // hauteur du gardien (étages pré-remplis à l'ouverture), 0 = aucun
}

// L'ascension : difficulté croissante, mutateurs alternés (bonus tôt, risque tard), gardiens introduits
// en milieu de parcours. Data-driven : on enrichit l'ascension en AJOUTANT une entrée.
export const STRATA: Stratum[] = [
  { id: 'seuil',    name: 'Le Seuil',     text: 'Efface 6 lignes pour ouvrir l’ascension.',           goalKind: 'lines',     goalN: 6,      mutator: 'neutre',     guardian: 0 },
  { id: 'veine',    name: 'La Veine',     text: 'Déclenche une cascade (chaîne ≥ 2).',                 goalKind: 'chain',     goalN: 2,      mutator: 'gravite',    guardian: 0 },
  { id: 'croisee',  name: 'La Croisée',   text: 'Réussis 1 Croix (deux axes effacés d’un coup).',      goalKind: 'cross',     goalN: 1,      mutator: 'flux',       guardian: 1 },
  { id: 'remous',   name: 'Le Remous',    text: 'Déclenche une cascade de chaîne ≥ 3.',                goalKind: 'chain',     goalN: 3,      mutator: 'resonance',  guardian: 1 },
  { id: 'arche',    name: 'L’Arche',      text: 'Atteins 25 000 points.',                              goalKind: 'score',     goalN: 25_000, mutator: 'densite',    guardian: 2 },
  { id: 'cont',     name: 'Le Fil',       text: 'Enchaîne un back-to-back de maîtrise ≥ 4.',           goalKind: 'continuum', goalN: 4,      mutator: 'main_legere',guardian: 2 },
  { id: 'apex',     name: 'L’Apex',       text: 'Réussis 2 Croix pour conquérir le sommet.',          goalKind: 'cross',     goalN: 2,      mutator: 'tempete',    guardian: 3 },
];

export function strataCount(): number { return STRATA.length; }
export function stratumByIndex(i: number): Stratum | undefined { return STRATA[i]; }

// Avancement d'une run vis-à-vis de l'objectif de strate (mêmes compteurs que main.ts / challenges).
export interface StratumProgress { lines: number; score: number; cross: number; chain: number; continuum: number; perfects: number; slabs: number; }

function metricValue(kind: ChallengeGoalKind, p: StratumProgress): number {
  switch (kind) {
    case 'lines': return p.lines; case 'score': return p.score; case 'cross': return p.cross;
    case 'chain': return p.chain; case 'continuum': return p.continuum; case 'perfect': return p.perfects;
    case 'slabs': return p.slabs; default: return 0;
  }
}

// L'objectif de la strate est-il atteint ? (≥ seuil sur la métrique concernée.)
export function stratumMet(s: Stratum, p: StratumProgress): boolean { return metricValue(s.goalKind, p) >= s.goalN; }

// Avancement borné 0..1 + libellé « courant / cible » (locale-neutre ; main.ts l'habille).
export interface StratumHud { ratio: number; label: string; }
export function stratumProgress(s: Stratum, p: StratumProgress): StratumHud {
  const v = metricValue(s.goalKind, p);
  return { ratio: s.goalN > 0 ? Math.min(1, v / s.goalN) : 1, label: Math.min(v, s.goalN) + '/' + s.goalN };
}

// GARDIEN — configuration STATIQUE pré-placée à l'ouverture d'une strate (PAS une montée d'instabilité).
// Remplit les `guardian` étages du bas d'un damier TROUÉ : motif déterministe `(x+z+y) % 3 !== 0` →
// dense mais ajouré, donc DÉMANTELABLE (chaque rangée a des trous à compléter, pas un mur infranchissable).
// Renvoie les cellules [x,y,z] à poser (main.ts leur affecte une couleur). Pur, déterministe, borné au puits.
export function guardianFill(s: Stratum): number[][] {
  const out: number[][] = [];
  const h = Math.max(0, Math.min(HEIGHT - 2, s.guardian | 0)); // jamais jusqu'au sommet (laisse respirer)
  for (let y = 0; y < h; y++) for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) {
    if (((x + z + y) % 3) !== 0) out.push([x, y, z]);
  }
  return out;
}

// Une strate est-elle débloquée au vu de l'index d'ascension courant (0-based) ? Séquentiel.
export function stratumUnlocked(index: number, ascent: number): boolean { return index <= Math.max(0, ascent | 0); }

// RET-STRATES-NGPLUS-001 — VRAI NG+ : chaque ascension complétée débloque une « Ascension+ »
// plus exigeante (et plus rémunératrice). On escalade via les leviers de run EXISTANTS — la chute
// (speedMult, <1 = plus rapide) et le score (scoreMult) — appliqués UNE fois à l'ouverture de la run
// (jamais par strate → pas de compounding). Bornés (NG+0..NG+MAX) pour rester jouables. AUCUN rapport
// avec la montée d'instabilité (gelée) : on accélère la chute, on n'injecte aucun bloc.
export const NGPLUS_MAX = 9;
export interface NgPlusBuffs { speedMult: number; scoreMult: number; }
export function ngPlusBuffs(tier: number): NgPlusBuffs {
  const t = Math.max(0, Math.min(NGPLUS_MAX, Math.floor(Number.isFinite(tier) ? tier : 0)));
  return { speedMult: Math.pow(0.9, t), scoreMult: 1 + t * 0.12 };
}
// Libellé court d'un palier NG+ (NG+0 = ascension de base, sans suffixe).
export function ngPlusLabel(tier: number): string {
  const t = Math.max(0, Math.min(NGPLUS_MAX, Math.floor(Number.isFinite(tier) ? tier : 0)));
  return t > 0 ? 'NG+' + t : '';
}
